import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { PencilLine, Sparkle, ArrowRight, CheckCircle } from "@phosphor-icons/react";

export default function MainsList() {
    const [prompts, setPrompts] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [filter, setFilter] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/mains/prompts");
            setPrompts(data);
        })();
        (async () => {
            try {
                const { data } = await api.get("/mains/submissions");
                setSubmissions(data);
            } catch {}
        })();
    }, []);

    const filtered = prompts.filter((p) => !filter || p.exam_id === filter);

    return (
        <Layout>
            <div className="mb-8">
                <div className="text-xs uppercase tracking-widest text-slate-500">Mains Writing</div>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl mt-1">Write. Submit. Get a rubric.</h1>
                <p className="text-slate-500 mt-2 text-sm">Claude Sonnet 4.5 evaluates every submission across five dimensions.</p>
            </div>

            <div className="mb-6 flex flex-wrap gap-2">
                {[["", "All"], ["kpsc_kas", "KAS"], ["kpsc_psi", "PSI"]].map(([v, l]) => (
                    <button
                        key={l}
                        onClick={() => setFilter(v)}
                        data-testid={`mains-filter-${l}`}
                        className={`px-4 py-1.5 rounded-full text-sm border ${filter === v ? "border-[#1E3A8A] text-[#1E3A8A] bg-[#1E3A8A]/5" : "border-[#BFD3FF] text-slate-600"}`}
                    >
                        {l}
                    </button>
                ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                    <h2 className="font-heading text-xl font-semibold mb-2">Prompts</h2>
                    {filtered.map((p) => (
                        <div key={p.prompt_id} className="bento-card">
                            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500">
                                <span className="text-[#1E3A8A]">{p.subject}</span>
                                <span>·</span>
                                <span>{p.difficulty}</span>
                                <span>·</span>
                                <span className="font-mono">{p.marks} marks</span>
                            </div>
                            <div className="font-heading text-lg mt-2 leading-snug">{p.prompt}</div>
                            <button
                                data-testid={`write-btn-${p.prompt_id}`}
                                onClick={() => navigate(`/mains/write/${p.prompt_id}`)}
                                className="btn-primary mt-4 text-sm py-2"
                            >
                                <PencilLine size={16} weight="duotone" /> Start writing <ArrowRight size={16} />
                            </button>
                        </div>
                    ))}
                </div>
                <aside>
                    <h2 className="font-heading text-xl font-semibold mb-3">Your Submissions</h2>
                    {submissions.length === 0 && <div className="text-sm text-slate-500 bento-card">Nothing yet — pick a prompt and write your first answer.</div>}
                    <div className="space-y-3">
                        {submissions.map((s) => (
                            <button
                                key={s.submission_id}
                                onClick={() => navigate(`/mains/${s.submission_id}`)}
                                data-testid={`submission-${s.submission_id}`}
                                className="w-full text-left bento-card"
                            >
                                <div className="text-[10px] uppercase tracking-widest text-[#1E3A8A]">{s.subject}</div>
                                <div className="text-sm text-slate-800 line-clamp-2 mt-1">{s.prompt}</div>
                                <div className="flex justify-between mt-3 text-xs">
                                    <span className="font-mono text-slate-500">{s.word_count}w</span>
                                    <span className={`font-mono ${s.evaluation?.marks_awarded ? "text-[#1E3A8A]" : "text-slate-500"}`}>
                                        {s.evaluation?.marks_awarded ? `${s.evaluation.marks_awarded} marks` : "Not evaluated"}
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                </aside>
            </div>
        </Layout>
    );
}
